import type { ApolloErrorLike } from '@bloomays-lib/utils.shared';
import { extractApolloErrorCode, getErrorStatusCode, isAbortError } from '@bloomays-lib/utils.shared';
import { isAuth0TokenRefreshError } from '../services/authService';

export interface CreateApolloErrorFilterOptions {
  /**
   * Business error codes that should be filtered out from monitoring.
   * They are usually handled by dedicated UX flows (session refresh, unknown
   * user redirect, etc.) and would only add noise in Sentry.
   */
  businessCodesToIgnore?: string[];
  /**
   * When `true`, HTTP responses with a status code < 500 are filtered out too
   * (default). Client-side responses (400-499) rarely deserve a Sentry alert.
   */
  ignoreClientErrors?: boolean;
}

/**
 * Builds an `ApolloClientConfig.filterExceptions` predicate that mirrors the
 * legacy `form.bloomers` behaviour but is reusable across apps.
 *
 * Returning `true` means "filter this error out" (don't report to Sentry). The
 * predicate always filters out Auth0 token refresh failures (handled by the
 * Apollo provider itself) and intentional fetch aborts (see `isAbortError`).
 *
 * The Apollo error inspection primitives themselves (`isAbortError`,
 * `extractApolloErrorCode`, `getApolloErrorCode`, `getApolloErrorStatusCode`,
 * `getApolloErrorExtensions`, `translateApolloError`) live in
 * `@bloomays-lib/utils.shared` and can be imported directly from there —
 * `common.auth` only keeps the auth-aware composition here.
 */
export const createApolloErrorFilter = (
  options: CreateApolloErrorFilterOptions = {},
): ((error: ApolloErrorLike) => boolean) => {
  const { businessCodesToIgnore = [], ignoreClientErrors = true } = options;
  const ignoreSet = new Set(businessCodesToIgnore);

  return (error: ApolloErrorLike): boolean => {
    if (isAuth0TokenRefreshError(error)) return true;
    if (isAbortError(error)) return true;

    const code = extractApolloErrorCode(error);
    if (code && ignoreSet.has(code)) return true;

    if (ignoreClientErrors && getErrorStatusCode(error) < 500) return true;

    return false;
  };
};
